'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface FieldErrors {
  fullName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

function validateEmail(email: string): string | undefined {
  if (!email) return 'Email is required.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Enter a valid email address.';
}

function validatePassword(password: string, isSignup: boolean): string | undefined {
  if (!password) return 'Password is required.';
  if (isSignup && password.length < 8) return 'Password must be at least 8 characters.';
  if (!isSignup && password.length < 1) return 'Password is required.';
}

type ApplicationRole = 'teacher' | 'moderator';

const roleInfo: Record<ApplicationRole, { title: string; description: string; color: string; activeColor: string }> = {
  teacher: {
    title: 'Teacher',
    description: 'Create and lead workshops, guide students through legal concepts, and develop educational content.',
    color: 'border-blue-200 bg-blue-50',
    activeColor: 'border-blue-500 bg-blue-50 ring-2 ring-blue-200',
  },
  moderator: {
    title: 'Moderator',
    description: 'Help maintain a safe community space, review flagged content, and support community guidelines.',
    color: 'border-purple-200 bg-purple-50',
    activeColor: 'border-purple-500 bg-purple-50 ring-2 ring-purple-200',
  },
};

export default function LoginPage() {
  const router = useRouter();
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState<'login' | 'signup'>('login');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Role application state
  const [applyForRole, setApplyForRole] = useState(false);
  const [selectedRole, setSelectedRole] = useState<ApplicationRole>('teacher');
  const [motivation, setMotivation] = useState('');
  const [experience, setExperience] = useState('');

  const switchMode = (newMode: 'login' | 'signup') => {
    setMode(newMode);
    setServerError('');
    setSuccessMessage('');
    setFieldErrors({});
    setTouched({});
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFullName('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setApplyForRole(false);
    setSelectedRole('teacher');
    setMotivation('');
    setExperience('');
  };

  const validateAll = (): FieldErrors => {
    const errors: FieldErrors = {};
    if (mode === 'signup' && !fullName.trim()) errors.fullName = 'Full name is required.';
    const emailErr = validateEmail(email);
    if (emailErr) errors.email = emailErr;
    const passErr = validatePassword(password, mode === 'signup');
    if (passErr) errors.password = passErr;
    if (mode === 'signup') {
      if (!confirmPassword) errors.confirmPassword = 'Please confirm your password.';
      else if (confirmPassword !== password) errors.confirmPassword = 'Passwords do not match.';
    }
    return errors;
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errors = validateAll();
    setFieldErrors(errors);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError('');
    setSuccessMessage('');

    const allTouched: Record<string, boolean> = { email: true, password: true };
    if (mode === 'signup') { allTouched.fullName = true; allTouched.confirmPassword = true; }
    setTouched(allTouched);

    const errors = validateAll();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    try {
      if (mode === 'login') {
        await signIn(email, password);
        router.push('/community-space');
        router.refresh();
      } else {
        await signUp(email, password, { fullName: fullName.trim() });

        // If user opted to apply for a role, submit the application
        if (applyForRole && motivation.trim()) {
          try {
            const supabase = createClient();
            // We need to get the newly created user — sign them in temporarily
            // Store application data in localStorage to submit after email confirmation + first login
            const pendingApp = {
              full_name: fullName.trim(),
              email: email.trim(),
              role: selectedRole,
              motivation: motivation.trim(),
              experience: experience.trim() || null,
            };
            localStorage.setItem('pendingRoleApplication', JSON.stringify(pendingApp));
          } catch {
            // Non-critical — don't block signup
          }
        }

        const roleMsg = applyForRole && motivation.trim()
          ? 'Your role application has been saved and will be submitted when you confirm your email and sign in.' :'';
        setSuccessMessage(`Account created! Check your email to confirm your address, then sign in.${roleMsg}`);
        switchMode('login');
      }
    } catch (err: any) {
      const msg: string = err?.message || '';
      if (msg.toLowerCase().includes('invalid login credentials') || msg.toLowerCase().includes('invalid credentials')) {
        setServerError('Incorrect email or password. Please try again.');
      } else if (msg.toLowerCase().includes('email already registered') || msg.toLowerCase().includes('user already registered')) {
        setServerError('An account with this email already exists. Try signing in instead.');
      } else if (msg.toLowerCase().includes('email not confirmed')) {
        router.push(`/verify-email?email=${encodeURIComponent(email)}`);
        return;
      } else if (msg.toLowerCase().includes('rate limit') || msg.toLowerCase().includes('too many')) {
        setServerError('Too many attempts. Please wait a moment and try again.');
      } else {
        setServerError(msg || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (field: keyof FieldErrors) =>
    `input-base ${touched[field] && fieldErrors[field] ? 'border-red-400 focus:ring-red-300' : ''}`;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <Link href="/" className="flex items-center gap-2.5 mb-4">
            <AppLogo size={40} />
            <span className="font-extrabold text-2xl tracking-tight text-primary">Project Concord</span>
          </Link>
          <h1 className="text-xl font-bold text-foreground">
            {mode === 'login' ? 'Welcome back' : 'Join the movement'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {mode === 'login' ? 'Sign in to continue your advocacy journey' : 'Create your account to start engaging'}
          </p>
        </div>

        {/* Card */}
        <div className="card-base p-6 shadow-lg">
          {/* Mode toggle */}
          <div className="flex rounded-xl bg-zinc-100 p-1 mb-6">
            <button
              type="button"
              onClick={() => switchMode('login')}
              suppressHydrationWarning
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
                mode === 'login' ? 'bg-white text-foreground shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => switchMode('signup')}
              suppressHydrationWarning
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
                mode === 'signup' ? 'bg-white text-foreground shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* Success message */}
          {successMessage && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-green-50 border border-green-200 mb-4">
              <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-green-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-green-700">{successMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* Full Name (signup only) */}
            {mode === 'signup' && (
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  Full Name <span className="text-primary">*</span>
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  onBlur={() => handleBlur('fullName')}
                  className={inputClass('fullName')}
                  placeholder="Your full name"
                  autoComplete="name"
                  suppressHydrationWarning
                />
                {touched.fullName && fieldErrors.fullName && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <Icon name="ExclamationCircleIcon" size={12} variant="solid" className="flex-shrink-0" />
                    {fieldErrors.fullName}
                  </p>
                )}
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">
                Email <span className="text-primary">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => handleBlur('email')}
                className={inputClass('email')}
                placeholder="you@example.com"
                autoComplete="email"
                suppressHydrationWarning
              />
              {touched.email && fieldErrors.email && (
                <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={12} variant="solid" className="flex-shrink-0" />
                  {fieldErrors.email}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-semibold text-foreground">
                  Password <span className="text-primary">*</span>
                </label>
                {mode === 'login' && (
                  <span className="text-xs text-muted-foreground">
                    Forgot password?{' '}
                    <a href="mailto:support@projectconcord.org" className="text-primary hover:underline">
                      Contact support
                    </a>
                  </span>
                )}
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  suppressHydrationWarning
                  onBlur={() => handleBlur('password')}
                  className={`${inputClass('password')} pr-10`}
                  placeholder={mode === 'signup' ? 'At least 8 characters' : 'Your password'}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} variant="outline" />
                </button>
              </div>
              {touched.password && fieldErrors.password && (
                <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={12} variant="solid" className="flex-shrink-0" />
                  {fieldErrors.password}
                </p>
              )}
              {mode === 'signup' && !fieldErrors.password && password.length > 0 && (
                <div className="mt-2">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          password.length >= level * 3
                            ? password.length >= 12
                              ? 'bg-green-500'
                              : password.length >= 8
                              ? 'bg-yellow-400' :'bg-red-400' :'bg-zinc-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {password.length < 8 ? 'Too short' : password.length < 12 ? 'Fair — consider a longer password' : 'Strong password'}
                  </p>
                </div>
              )}
            </div>

            {/* Confirm Password (signup only) */}
            {mode === 'signup' && (
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">
                  Confirm Password <span className="text-primary">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onBlur={() => handleBlur('confirmPassword')}
                    className={`${inputClass('confirmPassword')} pr-10`}
                    placeholder="Re-enter your password"
                    autoComplete="new-password"
                    suppressHydrationWarning
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                    tabIndex={-1}
                    aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                  >
                    <Icon name={showConfirmPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} variant="outline" />
                  </button>
                </div>
                {touched.confirmPassword && fieldErrors.confirmPassword && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <Icon name="ExclamationCircleIcon" size={12} variant="solid" className="flex-shrink-0" />
                    {fieldErrors.confirmPassword}
                  </p>
                )}
                {touched.confirmPassword && !fieldErrors.confirmPassword && confirmPassword && (
                  <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                    <Icon name="CheckCircleIcon" size={12} variant="solid" className="flex-shrink-0" />
                    Passwords match
                  </p>
                )}
              </div>
            )}

            {/* Apply for Role — signup only */}
            {mode === 'signup' && (
              <div className="border border-border rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setApplyForRole((v) => !v)}
                  suppressHydrationWarning
                  className="w-full flex items-center justify-between px-4 py-3 bg-zinc-50 hover:bg-zinc-100 transition-colors text-left"
                >
                  <div className="flex items-center gap-2.5">
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${applyForRole ? 'bg-primary border-primary' : 'border-zinc-300 bg-white'}`}>
                      {applyForRole && (
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">Apply to be a Teacher or Moderator</p>
                      <p className="text-xs text-muted-foreground">Optional — join the Project Concord team</p>
                    </div>
                  </div>
                  <Icon
                    name={applyForRole ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                    size={16}
                    variant="outline"
                    className="text-zinc-400 flex-shrink-0"
                  />
                </button>

                {applyForRole && (
                  <div className="px-4 pb-4 pt-3 space-y-4 bg-white border-t border-border">
                    {/* Role selection */}
                    <div>
                      <p className="text-xs font-semibold text-foreground mb-2">Which role are you applying for?</p>
                      <div className="grid grid-cols-2 gap-2">
                        {(Object.keys(roleInfo) as ApplicationRole[]).map((role) => (
                          <button
                            key={role}
                            type="button"
                            suppressHydrationWarning
                            onClick={() => setSelectedRole(role)}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              selectedRole === role ? roleInfo[role].activeColor : roleInfo[role].color
                            }`}
                          >
                            <p className="text-xs font-bold text-foreground">{roleInfo[role].title}</p>
                            <p className="text-xs text-zinc-500 mt-0.5 leading-tight">{roleInfo[role].description}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Motivation */}
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-1.5">
                        Why do you want to be a {roleInfo[selectedRole].title}? <span className="text-primary">*</span>
                      </label>
                      <textarea
                        value={motivation}
                        onChange={(e) => setMotivation(e.target.value)}
                        rows={3}
                        className="input-base resize-none text-sm"
                        placeholder={`Tell us why you'd like to be a ${roleInfo[selectedRole].title.toLowerCase()}…`}
                        suppressHydrationWarning
                      />
                      {applyForRole && !motivation.trim() && touched.confirmPassword && (
                        <p className="mt-1 text-xs text-amber-600">Please add a motivation or uncheck the role application.</p>
                      )}
                    </div>

                    {/* Experience */}
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-1.5">
                        Relevant experience <span className="text-zinc-400 font-normal">(optional)</span>
                      </label>
                      <textarea
                        value={experience}
                        onChange={(e) => setExperience(e.target.value)}
                        rows={2}
                        className="input-base resize-none text-sm"
                        placeholder="Any relevant background, skills, or experience…"
                        suppressHydrationWarning
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Server error */}
            {serverError && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
                <Icon name="ExclamationCircleIcon" size={16} variant="solid" className="text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">{serverError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3 text-sm justify-center"
              suppressHydrationWarning
            >
              {loading ? (
                <>
                  <Icon name="ArrowPathIcon" size={16} variant="outline" className="animate-spin" />
                  {mode === 'login' ? 'Signing in…' : 'Creating account…'}
                </>
              ) : (
                <>
                  <Icon name="ArrowRightIcon" size={16} variant="outline" />
                  {mode === 'login' ? 'Sign In' : 'Create Account'}
                </>
              )}
            </button>
          </form>

          {/* Demo credentials */}
          <div className="mt-5 pt-5 border-t border-border">
            <p className="text-xs text-muted-foreground text-center mb-2 font-medium">Demo account</p>
            <div className="flex items-center justify-between p-2.5 rounded-lg bg-zinc-50 border border-border">
              <div className="text-xs text-zinc-600">
                <span className="font-semibold">Email:</span> demo@projectconcord.org
              </div>
              <div className="text-xs text-zinc-600">
                <span className="font-semibold">Pass:</span> concord2026
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          <Link href="/" className="hover:text-primary transition-colors">← Back to home</Link>
        </p>
      </div>
    </div>
  );
}
